import { TravellerListItem } from '@/components/Travellers/TravellerListItem';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from '../core/Input';
import { IconUserPlus, IconDataImport } from '@/icons';
import { FC, useMemo, useState } from 'react';
import Fuse from 'fuse.js';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { ProfileCreateForm } from '../Travellers/ProfileCreateForm';
import { TravellerImportForm } from '../Travellers/TravellerImportForm';
import { ListCounter } from '../ListCounter';
import { DialogWindow } from '../Dialog';
import {
  useGetCollectionsForDepartment,
  useGetTravellersForDepartment,
  useListAllpayUsers,
  useListCardCredits,
} from '@/api/trafikportalen';
import { Loading } from '@/components/Loading.tsx';
import { collator } from '@/utils/collator';
import { hasDepartmentWriteAccess } from '@/helpers/departmentPermissions';
import Button from '../core/Button';

interface Props {
  departmentId: string;
}

export const DepartmentTravellers: FC<Props> = ({ departmentId }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [importOpen, setImportOpen] = useState<boolean>(false);
  const [importProcessing, setImportProcessing] = useState(false);
  const [search, setSearch] = useState('');

  const { formatMessage } = useIntl();

  const { data: travellers, isLoading } =
    useGetTravellersForDepartment(departmentId);

  const { data: collections } = useGetCollectionsForDepartment(departmentId);

  const { data: allpayUsers } = useListAllpayUsers({
    department_id: departmentId,
  });
  const { data: cardCredits } = useListCardCredits();

  const processedCardCredits = useMemo(() => {
    return cardCredits?.reduce(
      (acc, card) => {
        if (!card.collection_member_id || card.department_id !== departmentId) {
          return acc;
        }

        const traveller = travellers?.find((t) =>
          t.memberships.find(
            (m) => m.collection_member_id === card.collection_member_id,
          ),
        );

        if (!traveller) {
          return acc;
        }

        acc[traveller.traveller.id] =
          (acc[traveller.traveller.id] || 0) + card.credit;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [cardCredits, departmentId, travellers]);

  const processedAllpayUsers = useMemo(() => {
    return allpayUsers?.reduce(
      (acc, user) => {
        if (user.department_id !== departmentId) {
          return acc;
        }

        const traveller = travellers?.find((t) =>
          t.memberships.find(
            (m) => m.collection_member_id === user.collection_member_id,
          ),
        );

        if (!traveller) {
          return acc;
        }

        acc[traveller.traveller.id] =
          (acc[traveller.traveller.id] || 0) + user.credit;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [allpayUsers, departmentId, travellers]);

  const sortedTravellers = useMemo(() => {
    if (!travellers) {
      return [];
    }

    return travellers
      .map((traveller) => ({ ...traveller, collections: [] }))
      .sort((a, b) =>
        collator.compare(
          String(a.traveller.first_name?.toLowerCase() || a.traveller.email),
          String(b.traveller.first_name?.toLowerCase() || b.traveller.email),
        ),
      );
  }, [travellers]);

  const [ref] = useAutoAnimateConditional<HTMLUListElement>();

  const masterCollection = useMemo(() => {
    if (!collections) {
      return null;
    }
    return collections.find((collection) => collection.type === 'master');
  }, [collections]);

  const fuse = useMemo(
    () =>
      new Fuse(sortedTravellers || [], {
        keys: [
          'traveller.first_name',
          'traveller.last_name',
          'traveller.email',
          'traveller.bobcat_private_email',
        ],
        threshold: 0.1,
      }),
    [sortedTravellers],
  );

  const searchResult = useMemo(() => {
    if (!search) {
      return sortedTravellers;
    }
    return fuse.search(search).map((result) => result.item);
  }, [fuse, search, sortedTravellers]);

  if (isLoading) {
    return <Loading />;
  }

  if (!travellers) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col py-4 md:py-8">
        <div className="mb-4 flex flex-row flex-wrap justify-between">
          <h2>
            <FormattedMessage defaultMessage="Resenärer" />
          </h2>
          {hasDepartmentWriteAccess(departmentId) && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                startIcon={<IconUserPlus />}
                onClick={() => setOpen(true)}
              >
                <FormattedMessage defaultMessage="Lägg till resenär" />
              </Button>
              <Button
                variant="outline"
                startIcon={<IconDataImport />}
                onClick={() => setImportOpen(true)}
              >
                <FormattedMessage defaultMessage="Importera resenärer" />
              </Button>
            </div>
          )}
        </div>
        <Input
          type="text"
          placeholder={formatMessage({
            defaultMessage: 'Sök på namn eller e-post',
          })}
          id="traveller-search"
          className="w-full"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={formatMessage({
            defaultMessage: 'Sök resenär på namn eller e-post',
          })}
        />
        <ul ref={ref} className="flex w-full flex-col gap-2 pb-4 md:flex-[4]">
          <li>
            <ListCounter
              totalItems={sortedTravellers}
              filteredItems={searchResult ?? []}
              type={<FormattedMessage defaultMessage="resenärer" />}
            />
          </li>
          {searchResult.length === 0 && (
            <li className="flex w-full justify-center italic text-text-700">
              <FormattedMessage defaultMessage="Inga resenärer hittades" />
            </li>
          )}
          {searchResult.map((traveller) => (
            <TravellerListItem
              key={traveller.traveller.id}
              traveller={traveller}
              allpayCredit={processedAllpayUsers?.[traveller.traveller.id]}
              cardCredit={processedCardCredits?.[traveller.traveller.id]}
              hideDepartment={true}
            />
          ))}
        </ul>
      </div>
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Lägg till resenär' })}
        content={
          <ProfileCreateForm
            departmentId={departmentId}
            collectionId={masterCollection?.id}
            onOpenChange={setOpen}
            canSelectDepartment={false}
          />
        }
        onOpenChange={setOpen}
        open={open}
      />
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Importera resenärer' })}
        content={
          importOpen ? (
            <TravellerImportForm
              departmentId={departmentId}
              onOpenChange={setImportOpen}
              onProcessingChange={setImportProcessing}
            />
          ) : null
        }
        onOpenChange={setImportOpen}
        open={importOpen}
        preventClose={importProcessing}
        className="max-w-5xl"
      />
    </>
  );
};
